import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const ShareModal = ({ code, onClose }) => {
  const [copied, setCopied] = useState(false);
  const referralUrl = `${window?.location?.origin}/register?ref=${code}`;
  
  const shareMessage = `Join me at our gym! Use my referral code ${code} to get started. Sign up here: ${referralUrl}`;

  const handleCopyMessage = () => {
    navigator?.clipboard?.writeText(shareMessage);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleEmailShare = () => {
    const subject = encodeURIComponent('Join Our Gym!');
    const body = encodeURIComponent(shareMessage);
    window?.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
  };

  const handleSMSShare = () => {
    const body = encodeURIComponent(shareMessage);
    window?.open(`sms:?body=${body}`, '_blank');
  };

  const handleSocialShare = (platform) => {
    const encodedMessage = encodeURIComponent(shareMessage);
    const encodedUrl = encodeURIComponent(referralUrl);
    
    const urls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodedMessage}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodedMessage}`
    };

    if (urls?.[platform]) {
      window?.open(urls?.[platform], '_blank', 'width=600,height=400');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-primary/10">
              <Icon name="Share2" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-foreground">Share Your Referral</h2>
              <p className="text-sm text-muted-foreground">Invite friends and earn rewards</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center h-8 w-8 rounded-lg hover:bg-muted transition-colors"
          >
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Referral Message */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Your Referral Message
            </label>
            <div className="bg-muted/50 border border-border rounded-lg p-4">
              <p className="text-sm text-foreground whitespace-pre-wrap">{shareMessage}</p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCopyMessage}
              iconName={copied ? 'Check' : 'Copy'}
              className="mt-2"
              fullWidth
            >
              {copied ? 'Copied!' : 'Copy Message'}
            </Button>
          </div>

          {/* Quick Share Options */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Quick Share
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={handleEmailShare}
                iconName="Mail"
                className="justify-start"
              >
                Email
              </Button>
              <Button
                variant="outline"
                onClick={handleSMSShare}
                iconName="MessageSquare"
                className="justify-start"
              >
                SMS
              </Button>
            </div>
          </div>

          {/* Social Media */}
          <div>
            <label className="text-sm font-medium text-foreground mb-3 block">
              Share on Social Media
            </label>
            <div className="grid grid-cols-2 gap-3">
              <Button
                variant="outline"
                onClick={() => handleSocialShare('facebook')}
                iconName="Facebook"
                className="justify-start"
              >
                Facebook
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSocialShare('twitter')}
                iconName="Twitter"
                className="justify-start"
              >
                Twitter
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSocialShare('linkedin')}
                iconName="Linkedin"
                className="justify-start"
              >
                LinkedIn
              </Button>
              <Button
                variant="outline"
                onClick={() => handleSocialShare('whatsapp')}
                iconName="MessageCircle"
                className="justify-start"
              >
                WhatsApp
              </Button>
            </div>
          </div>

          {/* Referral Link */}
          <div>
            <label className="text-sm font-medium text-foreground mb-2 block">
              Direct Link
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={referralUrl}
                readOnly
                className="flex-1 h-10 px-3 py-2 text-sm bg-muted border border-border rounded-md"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => {
                  navigator?.clipboard?.writeText(referralUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 2000);
                }}
              >
                <Icon name={copied ? 'Check' : 'Copy'} size={16} />
              </Button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-6 border-t border-border">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ShareModal;